function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}
function aiNode(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${systemPrompt}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}
function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}
function smsNode(
  id: string,
  label: string,
  y: number,
  to: string,
  message: string,
  x = 80,
): any {
  return n(id, "sendSMS", label, y, { sendSmsDetails: { to, message } }, x);
}
function sheetsNode(
  id: string,
  label: string,
  y: number,
  sheetTab: string,
  values: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/${sheetTab}!A1:append?valueInputOption=USER_ENTERED`,
        headers: [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "values", value: values }],
        variables: [
          {
            name: "updates.updatedRows",
            customName: "rows_logged",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}

export const ELITE_BATCH22_PART14: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // ─── 164. Price Drop Alert (Mailchimp, Pattern D) ──────────────────────

  "Price Drop Alert": {
    nodes: [
      trigger("Shopify webhook: Product price decreased", "webhook_app", {
        source: "shopify",
        event: "products/update",
        howItStarts:
          "Fires when Shopify detects a product price decrease, sending the product ID, previous price, new price, and variant details to the workflow.",
        setup:
          "Connect your Shopify store and enable the products/update webhook. The workflow filters for price decreases only.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "OpenAI: Extract price drop details and generate urgency copy",
        280,
        "You are an e-commerce pricing analyst. Extract the product name, old price, new price, percentage drop, product URL, and image URL from the webhook payload. Generate a compelling subject line and urgency copy emphasizing the savings. Return product_name, old_price, new_price, pct_drop, product_url, image_url, subject_line, urgency_copy.",
        "Analyze this Shopify product update webhook and extract price drop details: {webhook_payload}",
        [
          { field: "product_name", as: "product_name", type: "string" },
          { field: "old_price", as: "old_price", type: "string" },
          { field: "new_price", as: "new_price", type: "string" },
          { field: "pct_drop", as: "pct_drop", type: "number" },
          { field: "product_url", as: "product_url", type: "string" },
          { field: "subject_line", as: "subject_line", type: "string" },
          { field: "urgency_copy", as: "urgency_copy", type: "string" },
        ],
      ),
      n(
        "3",
        "hitApi",
        "Mailchimp: Lookup subscribers interested in this product",
        520,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://usX.api.mailchimp.com/3.0/segments/{segment_id}/members?count=500",
            headers: [
              { key: "Authorization", value: "Bearer {mailchimp_api_key}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [],
            variables: [
              {
                name: "members",
                customName: "interested_subscribers",
                type: "object",
              },
              {
                name: "total_items",
                customName: "subscriber_count",
                type: "number",
              },
            ],
          },
        },
      ),
      n("4", "condition", "Check: Price drop >= 15%?", 760, {
        conditionDetails: {
          conditions: [{ variable: "{pct_drop}", operator: ">=", value: "15" }],
        },
      }),
      emailNode(
        "5",
        "Mailchimp: Send price drop alert email with savings callout",
        1000,
        "{subscriber_email}",
        "{subject_line}",
        "Great news! {product_name} just dropped from {old_price} to {new_price} — that's {pct_drop}% off!\n\n{urgency_copy}\n\nShop now before the price goes back up: {product_url}\n\nThis alert was sent because you showed interest in this product.",
      ),
      n("6", "delayAction", "Wait 24 hours", 1240, {
        delayDetails: { duration: 24, unit: "hours" },
      }),
      n("7", "condition", "Check: Subscriber has not purchased yet?", 1480, {
        conditionDetails: {
          conditions: [
            {
              variable: "{purchase_completed}",
              operator: "==",
              value: "false",
            },
          ],
        },
      }),
      emailNode(
        "8",
        "Mailchimp: Send urgency follow-up with limited-time framing",
        1720,
        "{subscriber_email}",
        "Last chance: {product_name} is still {pct_drop}% off",
        "Hi {first_name},\n\nJust a reminder — {product_name} is still available at {new_price} (was {old_price}). We can't guarantee this price will last.\n\nOther shoppers are viewing this item right now. Don't miss your chance to save {pct_drop}%.\n\nGrab it now: {product_url}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
    summary:
      "Monitors Shopify for product price decreases, uses AI to extract drop details and generate urgency copy, verifies interested Mailchimp subscribers, sends an immediate alert for drops >= 15%, waits 24 hours, then sends a scarcity-based follow-up to non-purchasers to maximize alert-to-purchase conversion.",
  },

  // ─── 165. Back in Stock Alert (Mailchimp, Pattern D) ───────────────────

  "Back in Stock Alert": {
    nodes: [
      trigger("Shopify webhook: Product restocked", "webhook_app", {
        source: "shopify",
        event: "inventory_levels/update",
        howItStarts:
          "Fires when a previously out-of-stock Shopify product variant is restocked, providing the product ID, variant ID, new inventory quantity, and SKU.",
        setup:
          "Connect your Shopify store and enable the inventory_levels/update webhook. The workflow filters for items going from 0 to positive stock.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "OpenAI: Extract restocked product details and generate alert copy",
        280,
        "You are an e-commerce inventory analyst. Extract the product name, variant title, current stock quantity, price, product URL, and image URL from the restock webhook. Generate an exciting subject line and persuasive body copy emphasizing limited availability. Return product_name, variant_title, stock_qty, price, product_url, image_url, subject_line, alert_body.",
        "Analyze this Shopify inventory restock event and extract product details: {webhook_payload}",
        [
          { field: "product_name", as: "product_name", type: "string" },
          { field: "variant_title", as: "variant_title", type: "string" },
          { field: "stock_qty", as: "stock_qty", type: "number" },
          { field: "price", as: "price", type: "string" },
          { field: "product_url", as: "product_url", type: "string" },
          { field: "subject_line", as: "subject_line", type: "string" },
          { field: "alert_body", as: "alert_body", type: "string" },
        ],
      ),
      n(
        "3",
        "hitApi",
        "Mailchimp: Fetch waitlist subscribers for this product",
        520,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://usX.api.mailchimp.com/3.0/lists/{list_id}/segments/{waitlist_segment_id}/members?count=500",
            headers: [
              { key: "Authorization", value: "Bearer {mailchimp_api_key}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [],
            variables: [
              {
                name: "members",
                customName: "waitlist_members",
                type: "object",
              },
              {
                name: "total_items",
                customName: "waitlist_count",
                type: "number",
              },
            ],
          },
        },
      ),
      n(
        "4",
        "condition",
        "Check: Stock quantity is limited (< 50 units)?",
        760,
        {
          conditionDetails: {
            conditions: [
              { variable: "{stock_qty}", operator: "<", value: "50" },
            ],
          },
        },
      ),
      emailNode(
        "5",
        "Mailchimp: Send back-in-stock alert with scarcity note",
        1000,
        "{subscriber_email}",
        "{subject_line}",
        "It's back! {product_name} ({variant_title}) is now available again at {price}.\n\n{alert_body}\n\nOnly {stock_qty} units available — this item sold out quickly last time.\n\nShop now: {product_url}",
      ),
      n("6", "delayAction", "Wait 48 hours", 1240, {
        delayDetails: { duration: 48, unit: "hours" },
      }),
      n("7", "condition", "Check: Subscriber still has not purchased?", 1480, {
        conditionDetails: {
          conditions: [
            {
              variable: "{purchase_completed}",
              operator: "==",
              value: "false",
            },
          ],
        },
      }),
      emailNode(
        "8",
        "Mailchimp: Send final reminder with similar product alternatives",
        1720,
        "{subscriber_email}",
        "Still thinking about {product_name}? Stock is running low",
        "Hi {first_name},\n\n{product_name} ({variant_title}) is still available at {price}, but stock is getting low.\n\nIf this exact item isn't quite right, you might also like these similar products:\n\n- {similar_product_1}\n- {similar_product_2}\n- {similar_product_3}\n\nDon't wait too long: {product_url}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
    summary:
      "Detects Shopify inventory restocks, uses AI to extract product details and generate alert copy, fetches Mailchimp waitlist subscribers, sends an immediate back-in-stock email with scarcity framing for limited stock, waits 48 hours, then sends a follow-up with similar alternatives to non-purchasers.",
  },

  // ─── 166. Low Inventory Alert (Mailchimp, Pattern D) ───────────────────

  "Low Inventory Alert": {
    nodes: [
      trigger(
        "Shopify webhook: Inventory level drops below threshold",
        "webhook_app",
        {
          source: "shopify",
          event: "inventory_levels/update",
          howItStarts:
            "Fires when a Shopify product variant's inventory drops below a configured threshold (e.g., 10 units), sending the product ID, variant ID, remaining quantity, and SKU.",
          setup:
            "Connect your Shopify store and enable the inventory_levels/update webhook. Configure your low-stock threshold in Shopify or filter in the workflow.",
          inputFields: [],
        },
      ),
      aiNode(
        "2",
        "OpenAI: Extract low-stock product details and generate scarcity messaging",
        280,
        "You are an e-commerce urgency copywriter. Extract the product name, variant, remaining stock, price, product URL, and image URL. Generate a high-urgency subject line and body copy emphasizing scarcity and FOMO. Return product_name, variant_title, remaining_stock, price, product_url, image_url, subject_line, scarcity_copy.",
        "Analyze this low-inventory Shopify event and create scarcity-driven messaging: {webhook_payload}",
        [
          { field: "product_name", as: "product_name", type: "string" },
          { field: "variant_title", as: "variant_title", type: "string" },
          { field: "remaining_stock", as: "remaining_stock", type: "number" },
          { field: "price", as: "price", type: "string" },
          { field: "product_url", as: "product_url", type: "string" },
          { field: "subject_line", as: "subject_line", type: "string" },
          { field: "scarcity_copy", as: "scarcity_copy", type: "string" },
        ],
      ),
      n(
        "3",
        "hitApi",
        "Mailchimp: Fetch subscribers who browsed or wishlisted this product",
        520,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://usX.api.mailchimp.com/3.0/lists/{list_id}/segments/{browsed_segment_id}/members?count=500",
            headers: [
              { key: "Authorization", value: "Bearer {mailchimp_api_key}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [],
            variables: [
              {
                name: "members",
                customName: "interested_members",
                type: "object",
              },
              {
                name: "total_items",
                customName: "member_count",
                type: "number",
              },
            ],
          },
        },
      ),
      n("4", "condition", "Check: Stock critically low (< 5 units)?", 760, {
        conditionDetails: {
          conditions: [
            { variable: "{remaining_stock}", operator: "<", value: "5" },
          ],
        },
      }),
      emailNode(
        "5",
        "Mailchimp: Send low-inventory urgency alert",
        1000,
        "{subscriber_email}",
        "{subject_line}",
        "Heads up, {first_name} — {product_name} ({variant_title}) is almost gone!\n\nOnly {remaining_stock} left in stock at {price}.\n\n{scarcity_copy}\n\nGrab yours before it's gone: {product_url}",
      ),
      n("6", "delayAction", "Wait 12 hours", 1240, {
        delayDetails: { duration: 12, unit: "hours" },
      }),
      n(
        "7",
        "condition",
        "Check: Item still available and subscriber has not purchased?",
        1480,
        {
          conditionDetails: {
            conditions: [
              {
                variable: "{purchase_completed}",
                operator: "==",
                value: "false",
              },
              {
                variable: "{item_still_in_stock}",
                operator: "==",
                value: "true",
              },
            ],
          },
        },
      ),
      emailNode(
        "8",
        "Mailchimp: Send final last-chance email before sellout",
        1720,
        "{subscriber_email}",
        "Final warning: {product_name} is about to sell out",
        "Hi {first_name},\n\nThis is your last chance — {product_name} has fewer than {remaining_stock} units remaining. Once they're gone, we don't know when (or if) it will be restocked.\n\nAt {price}, this won't last much longer.\n\nBuy now: {product_url}\n\nWe'll remove you from this alert once the item sells out.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
    summary:
      "Monitors Shopify for inventory dropping below threshold, uses AI to generate scarcity messaging, targets Mailchimp subscribers who browsed or wishlisted the product, sends an immediate urgency alert for critically low stock (< 5 units), waits 12 hours, then sends a final last-chance email if the item is still available and unpurchased.",
  },

  // ─── 167. Post-Purchase Thank You (Mailchimp, Pattern E) ───────────────

  "Post-Purchase Thank You": {
    nodes: [
      trigger("Shopify webhook: New order placed", "webhook_app", {
        source: "shopify",
        event: "orders/create",
        howItStarts:
          "Fires when a customer places a new order on Shopify, providing order ID, customer email, line items, order total, shipping address, and payment status.",
        setup:
          "Connect your Shopify store and enable the orders/create webhook. This workflow triggers for every completed order.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "OpenAI: Extract order details and generate personalized thank-you content",
        280,
        "You are a customer experience specialist. Extract the customer first name, email, order number, product names (array), order total, and shipping method. Generate a warm, personalized thank-you message and product care tips specific to the items ordered. Return first_name, email, order_number, products, order_total, shipping_method, thank_you_message, care_tips.",
        "Analyze this Shopify order and generate a personalized thank-you with care tips: {webhook_payload}",
        [
          { field: "first_name", as: "first_name", type: "string" },
          { field: "email", as: "customer_email", type: "string" },
          { field: "order_number", as: "order_number", type: "string" },
          { field: "products", as: "products", type: "object" },
          { field: "order_total", as: "order_total", type: "string" },
          {
            field: "thank_you_message",
            as: "thank_you_message",
            type: "string",
          },
          { field: "care_tips", as: "care_tips", type: "string" },
        ],
      ),
      n(
        "3",
        "hitApi",
        "Mailchimp: Lookup customer profile and purchase history",
        520,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://usX.api.mailchimp.com/3.0/lists/{list_id}/members/{subscriber_hash}",
            headers: [
              { key: "Authorization", value: "Bearer {mailchimp_api_key}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [],
            variables: [
              {
                name: "merge_fields.PURCHASE_COUNT",
                customName: "purchase_count",
                type: "number",
              },
              {
                name: "merge_fields.TOTAL_SPENT",
                customName: "total_spent",
                type: "string",
              },
              {
                name: "status",
                customName: "subscription_status",
                type: "string",
              },
            ],
          },
        },
      ),
      n(
        "4",
        "condition",
        "Check: Is this the customer's first purchase?",
        760,
        {
          conditionDetails: {
            conditions: [
              { variable: "{purchase_count}", operator: "<=", value: "1" },
            ],
          },
        },
      ),
      emailNode(
        "5",
        "Mailchimp: Send personalized thank-you email with getting-started tips",
        1000,
        "{customer_email}",
        "Thank you for your order #{order_number}, {first_name}!",
        "{thank_you_message}\n\nOrder Summary:\n- Items: {products}\n- Total: {order_total}\n\nSince this is your first order with us, here are some tips to get the most out of your purchase:\n\n{care_tips}\n\nNeed help? Reply to this email and our team will get back to you within 24 hours.\n\nWelcome to the family!",
      ),
      n("6", "delayAction", "Wait 7 days for delivery", 1240, {
        delayDetails: { duration: 7, unit: "days" },
      }),
      n("7", "condition", "Check: Order has been delivered?", 1480, {
        conditionDetails: {
          conditions: [
            {
              variable: "{fulfillment_status}",
              operator: "==",
              value: "delivered",
            },
          ],
        },
      }),
      emailNode(
        "8",
        "Mailchimp: Send post-delivery check-in with usage tips",
        1720,
        "{customer_email}",
        "How's everything going with your order, {first_name}?",
        "Hi {first_name},\n\nYour order #{order_number} should have arrived by now. We hope you're loving your new purchase!\n\nHere are some advanced tips:\n{care_tips}\n\nIf anything isn't perfect, we're here to help. Just reply to this email.\n\nHappy with your purchase? We'd love to hear about it — leave a review and get 10% off your next order.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
    summary:
      "Triggers on new Shopify orders, uses AI to extract order details and generate personalized thank-you copy with care tips, checks Mailchimp for purchase history, tailors the message for first-time buyers, sends a warm thank-you email, waits 7 days for delivery, then sends a post-delivery check-in with advanced tips and review invitation.",
  },

  // ─── 168. Review Request Journey (Mailchimp, Pattern E) ────────────────

  "Review Request Journey": {
    nodes: [
      trigger("Shopify webhook: Order fulfilled and delivered", "webhook_app", {
        source: "shopify",
        event: "fulfillments/update",
        howItStarts:
          "Fires when a Shopify fulfillment is marked as delivered, providing the order ID, customer email, fulfilled line items, tracking number, and delivery date.",
        setup:
          "Connect your Shopify store and enable the fulfillments/update webhook. The workflow filters for delivery-confirmed events.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "OpenAI: Extract delivered items and generate review request copy",
        280,
        "You are a customer feedback specialist. Extract the customer first name, email, order number, delivered product names, delivery date, and order value. Generate a friendly review request subject line and body that explains why reviews matter and makes it easy. Return first_name, email, order_number, products, delivery_date, order_value, subject_line, review_request_body.",
        "Analyze this Shopify delivery confirmation and create a compelling review request: {webhook_payload}",
        [
          { field: "first_name", as: "first_name", type: "string" },
          { field: "email", as: "customer_email", type: "string" },
          { field: "order_number", as: "order_number", type: "string" },
          { field: "products", as: "products", type: "object" },
          { field: "delivery_date", as: "delivery_date", type: "string" },
          { field: "subject_line", as: "subject_line", type: "string" },
          {
            field: "review_request_body",
            as: "review_request_body",
            type: "string",
          },
        ],
      ),
      n(
        "3",
        "hitApi",
        "Mailchimp: Lookup customer VIP status and past review history",
        520,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://usX.api.mailchimp.com/3.0/lists/{list_id}/members/{subscriber_hash}",
            headers: [
              { key: "Authorization", value: "Bearer {mailchimp_api_key}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [],
            variables: [
              {
                name: "merge_fields.VIP",
                customName: "is_vip",
                type: "string",
              },
              {
                name: "merge_fields.REVIEWS_LEFT",
                customName: "reviews_left",
                type: "number",
              },
              {
                name: "merge_fields.PURCHASE_COUNT",
                customName: "purchase_count",
                type: "number",
              },
            ],
          },
        },
      ),
      n(
        "4",
        "condition",
        "Check: Customer has made multiple purchases (repeat buyer)?",
        760,
        {
          conditionDetails: {
            conditions: [
              { variable: "{purchase_count}", operator: ">=", value: "2" },
            ],
          },
        },
      ),
      emailNode(
        "5",
        "Mailchimp: Send review request email with direct review link",
        1000,
        "{customer_email}",
        "{subject_line}",
        "Hi {first_name},\n\nYour order #{order_number} was delivered on {delivery_date}. We hope you're enjoying your purchase!\n\n{review_request_body}\n\nAs a valued repeat customer, your honest feedback helps other shoppers and helps us improve.\n\nLeave a review (takes 2 minutes): {review_url}\n\nThank you for being part of our community!",
      ),
      n("6", "delayAction", "Wait 5 days", 1240, {
        delayDetails: { duration: 5, unit: "days" },
      }),
      n("7", "condition", "Check: Review has not been submitted yet?", 1480, {
        conditionDetails: {
          conditions: [
            { variable: "{review_submitted}", operator: "==", value: "false" },
          ],
        },
      }),
      emailNode(
        "8",
        "Mailchimp: Send gentle review reminder with incentive",
        1720,
        "{customer_email}",
        "Quick reminder: Share your thoughts on {products}",
        "Hi {first_name},\n\nWe noticed you haven't had a chance to review your recent purchase yet. No worries — we know life gets busy!\n\nYour feedback truly matters to us and to fellow shoppers. As a thank you for sharing your experience, we'll send you a 10% discount code for your next order.\n\nShare your review: {review_url}\n\nIt only takes 2 minutes. Thank you!",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
    summary:
      "Triggers when a Shopify order is delivered, uses AI to craft a personalized review request, checks Mailchimp for VIP status and purchase frequency, sends a tailored review email to repeat buyers with a direct link, waits 5 days, then sends a gentle reminder with a 10% incentive to those who haven't submitted a review.",
  },

  // ─── 169. Cross-Sell After Purchase (Mailchimp, Pattern E) ─────────────

  "Cross-Sell After Purchase": {
    nodes: [
      trigger("Shopify webhook: Order placed", "webhook_app", {
        source: "shopify",
        event: "orders/create",
        howItStarts:
          "Fires when a customer completes a purchase on Shopify, providing the order ID, customer email, purchased product IDs, categories, order total, and customer tags.",
        setup:
          "Connect your Shopify store and enable the orders/create webhook. The workflow analyzes the purchased items to recommend complementary products.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "OpenAI: Analyze purchase and identify cross-sell opportunities",
        280,
        "You are a product recommendation engine. Analyze the purchased products, their categories, price points, and common pairings. Identify the top 3 complementary products with reasoning. Generate a personalized cross-sell subject line and persuasive recommendation copy. Return first_name, email, order_number, purchased_products, cross_sell_products (array of 3 with name, price, url, reason), subject_line, recommendation_copy.",
        "Analyze this order and identify the best cross-sell products: {webhook_payload}",
        [
          { field: "first_name", as: "first_name", type: "string" },
          { field: "email", as: "customer_email", type: "string" },
          { field: "order_number", as: "order_number", type: "string" },
          {
            field: "purchased_products",
            as: "purchased_products",
            type: "object",
          },
          {
            field: "cross_sell_products",
            as: "cross_sell_products",
            type: "object",
          },
          { field: "subject_line", as: "subject_line", type: "string" },
          {
            field: "recommendation_copy",
            as: "recommendation_copy",
            type: "string",
          },
        ],
      ),
      n(
        "3",
        "hitApi",
        "Mailchimp: Lookup customer tenure and spending tier",
        520,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://usX.api.mailchimp.com/3.0/lists/{list_id}/members/{subscriber_hash}",
            headers: [
              { key: "Authorization", value: "Bearer {mailchimp_api_key}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [],
            variables: [
              {
                name: "merge_fields.PURCHASE_COUNT",
                customName: "purchase_count",
                type: "number",
              },
              {
                name: "merge_fields.TOTAL_SPENT",
                customName: "total_spent",
                type: "string",
              },
              {
                name: "merge_fields.VIP",
                customName: "is_vip",
                type: "string",
              },
            ],
          },
        },
      ),
      n("4", "condition", "Check: Is this a first-time buyer?", 760, {
        conditionDetails: {
          conditions: [
            { variable: "{purchase_count}", operator: "<=", value: "1" },
          ],
        },
      }),
      emailNode(
        "5",
        "Mailchimp: Send cross-sell recommendations with education angle",
        1000,
        "{customer_email}",
        "{subject_line}",
        "Hi {first_name},\n\nThank you for your recent order #{order_number}! Based on what you purchased, we think you'll love these complementary products:\n\n{recommendation_copy}\n\nThese items are popular with customers who bought similar products. As a first-time buyer, enjoy free shipping on your next order when you add any of these to your collection.\n\nShop recommendations: {shop_url}",
      ),
      n("6", "delayAction", "Wait 10 days", 1240, {
        delayDetails: { duration: 10, unit: "days" },
      }),
      n(
        "7",
        "condition",
        "Check: Customer has not made a second purchase?",
        1480,
        {
          conditionDetails: {
            conditions: [
              {
                variable: "{second_purchase_made}",
                operator: "==",
                value: "false",
              },
            ],
          },
        },
      ),
      emailNode(
        "8",
        "Mailchimp: Send product education email with social proof",
        1720,
        "{customer_email}",
        "Customers who bought {purchased_products} also loved these",
        "Hi {first_name},\n\nWe wanted to share something interesting — customers who bought the same items as you frequently pair them with these:\n\n{cross_sell_products}\n\nHere's what one customer said: 'I wish I'd bought these together from the start!'\n\nExplore the full collection: {shop_url}\n\nP.S. Your free shipping offer is still active for a limited time.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
    summary:
      "Triggers on Shopify orders, uses AI to analyze the purchase and identify top 3 complementary products, checks Mailchimp for customer tenure and spending tier, sends personalized cross-sell recommendations with a first-buyer incentive, waits 10 days, then follows up with social proof and education if no second purchase occurred.",
  },

  // ─── 170. Replenishment Reminder (Mailchimp, Pattern F) ────────────────

  "Replenishment Reminder": {
    nodes: [
      trigger(
        "Schedule: Check for customers approaching predicted reorder date",
        "schedule",
        {
          schedule: "Every day at 9:00 AM",
          howItStarts:
            "Runs daily to identify customers whose consumable products are nearing their predicted reorder window based on average product consumption cycles and past purchase frequency.",
          setup:
            "Set up a daily schedule trigger at 9:00 AM. Configure your product reorder cycles in Shopify or use the AI node to estimate them.",
          inputFields: [],
        },
      ),
      aiNode(
        "2",
        "OpenAI: Identify customers due for replenishment and generate reminders",
        280,
        "You are a replenishment timing analyst. Given the customer list with last purchase dates, product consumption cycles, and reorder windows, identify customers who are within 7 days of their predicted reorder date. For each, generate a personalized subject line and reminder copy. Return customers_due (array with email, first_name, product_name, days_until_reorder, reorder_url), batch_count, reminder_template.",
        "Analyze these customers and their purchase history to find those due for replenishment: {customer_batch}",
        [
          { field: "customers_due", as: "customers_due", type: "object" },
          { field: "batch_count", as: "batch_count", type: "number" },
          {
            field: "reminder_template",
            as: "reminder_template",
            type: "string",
          },
        ],
      ),
      n(
        "3",
        "hitApi",
        "Stripe: Verify customer payment method is active",
        520,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://api.stripe.com/v1/customers/{stripe_customer_id}/payment_methods?type=card",
            headers: [
              { key: "Authorization", value: "Bearer {stripe_secret_key}" },
              {
                key: "Content-Type",
                value: "application/x-www-form-urlencoded",
              },
            ],
            body: [],
            variables: [
              {
                name: "data[0].id",
                customName: "payment_method_id",
                type: "string",
              },
              {
                name: "data[0].card.last4",
                customName: "card_last4",
                type: "string",
              },
              { name: "has_more", customName: "has_payment", type: "boolean" },
            ],
          },
        },
      ),
      n(
        "4",
        "condition",
        "Check: Customer has a valid payment method on file?",
        760,
        {
          conditionDetails: {
            conditions: [
              { variable: "{has_payment}", operator: "==", value: "true" },
            ],
          },
        },
      ),
      emailNode(
        "5",
        "Mailchimp: Send replenishment reminder with one-click reorder",
        1000,
        "{customer_email}",
        "Time to restock your {product_name}, {first_name}!",
        "Hi {first_name},\n\nBased on your usage pattern, you're about {days_until_reorder} days away from running out of {product_name}.\n\nWe've made it easy to reorder — just click below to restock at the same great price. Your payment method ending in {card_last4} is ready to go.\n\nReorder now: {reorder_url}\n\nPro tip: Subscribe and save 15% — never worry about running out again!",
      ),
      n("6", "delayAction", "Wait 5 days", 1240, {
        delayDetails: { duration: 5, unit: "days" },
      }),
      n("7", "condition", "Check: Customer has not reordered yet?", 1480, {
        conditionDetails: {
          conditions: [
            { variable: "{reorder_completed}", operator: "==", value: "false" },
          ],
        },
      }),
      emailNode(
        "8",
        "Mailchimp: Send last-chance reminder with subscribe-and-save offer",
        1720,
        "{customer_email}",
        "Don't run out of {product_name} — reorder today and save 15%",
        "Hi {first_name},\n\nJust a friendly heads up — you're likely running low on {product_name} by now.\n\nOrder today and we'll include free expedited shipping so you don't miss a day. Even better, switch to our subscribe-and-save plan and get 15% off every order, forever.\n\nReorder now: {reorder_url}\n\nSubscribe & save: {subscribe_url}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
    summary:
      "Runs daily to identify customers approaching predicted reorder dates, uses AI to batch-analyze consumption patterns, verifies active Stripe payment methods, sends a personalized replenishment reminder with one-click reorder, waits 5 days, then follows up with a subscribe-and-save offer and free shipping incentive for non-reorderers.",
  },

  // ─── 171. Birthday Journey (Mailchimp, Pattern H) ─────────────────────

  "Birthday Journey": {
    nodes: [
      trigger(
        "Schedule: Check for customer birthdays approaching in 3 days",
        "schedule",
        {
          schedule: "Every day at 7:00 AM",
          howItStarts:
            "Runs daily to identify Mailchimp subscribers whose birthday is in 3 days, pulling their profile data, purchase history, and loyalty tier to personalize the birthday offer.",
          setup:
            "Set up a daily schedule trigger at 7:00 AM. Ensure Mailchimp merge field BIRTHDAY is populated for subscribers.",
          inputFields: [],
        },
      ),
      n(
        "2",
        "hitApi",
        "Mailchimp: Fetch subscribers with birthdays in 3 days and their LTV",
        280,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://usX.api.mailchimp.com/3.0/lists/{list_id}/members?count=100&birthday_month={target_month}&birthday_day={target_day}",
            headers: [
              { key: "Authorization", value: "Bearer {mailchimp_api_key}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [],
            variables: [
              {
                name: "members",
                customName: "birthday_members",
                type: "object",
              },
              {
                name: "total_items",
                customName: "birthday_count",
                type: "number",
              },
            ],
          },
        },
      ),
      n(
        "3",
        "condition",
        "Check: Customer is VIP tier (total spent > $500)?",
        520,
        {
          conditionDetails: {
            conditions: [
              { variable: "{total_spent}", operator: ">", value: "500" },
            ],
          },
        },
      ),
      emailNode(
        "4",
        "Mailchimp: Send VIP birthday email with exclusive 25% off offer",
        760,
        "{subscriber_email}",
        "Happy Birthday, {first_name}! Here's a special VIP gift just for you",
        "Happy Birthday, {first_name}!\n\nAs one of our most valued VIP customers, we want to make your day extra special.\n\nHere's your exclusive birthday gift: 25% off your entire order with code BDAY25VIP.\n\nThis code is valid for the next 7 days — treat yourself to something amazing!\n\nShop your birthday gift: {shop_url}\n\nWishing you a wonderful birthday from the entire team.",
      ),
      n("5", "delayAction", "Wait 5 days", 1000, {
        delayDetails: { duration: 5, unit: "days" },
      }),
      n(
        "6",
        "condition",
        "Check: Birthday discount has not been redeemed?",
        1240,
        {
          conditionDetails: {
            conditions: [
              {
                variable: "{discount_redeemed}",
                operator: "==",
                value: "false",
              },
            ],
          },
        },
      ),
      emailNode(
        "7",
        "Mailchimp: Send birthday offer redemption reminder",
        1480,
        "{subscriber_email}",
        "Your birthday gift expires in 2 days, {first_name}!",
        "Hi {first_name},\n\nWe hope you had a fantastic birthday! Just a quick reminder that your exclusive birthday offer — 25% off with code BDAY25VIP — expires in 2 days.\n\nDon't let it go to waste. Here are some items we think you'd love:\n\n- {recommended_product_1}\n- {recommended_product_2}\n- {recommended_product_3}\n\nUse your gift: {shop_url}",
      ),
      sheetsNode(
        "8",
        "Google Sheets: Log birthday campaign result",
        1720,
        "BirthdayCampaigns",
        '[["{subscriber_email}", "{first_name}", "{birthday_date}", "{is_vip}", "{discount_redeemed}", "{order_value}", "{timestamp}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("7", "8"),
    ],
    summary:
      "Runs daily to find subscribers with upcoming birthdays, fetches their Mailchimp profiles and LTV, sends VIP-tier customers an exclusive 25% birthday offer, waits 5 days, sends a redemption reminder if unused, then logs the birthday campaign outcome to Google Sheets for tracking redemption rates.",
  },

  // ─── 172. Anniversary Offer Journey (Mailchimp, Pattern H) ─────────────

  "Anniversary Offer Journey": {
    nodes: [
      trigger(
        "Schedule: Check for customer sign-up anniversaries",
        "schedule",
        {
          schedule: "Every day at 8:00 AM",
          howItStarts:
            "Runs daily to identify Mailchimp subscribers whose account anniversary (sign-up date) falls within the next 3 days, pulling their profile data, total orders, and lifetime value.",
          setup:
            "Set up a daily schedule trigger at 8:00 AM. Ensure Mailchimp merge fields include SIGNUP_DATE, PURCHASE_COUNT, and TOTAL_SPENT.",
          inputFields: [],
        },
      ),
      n(
        "2",
        "hitApi",
        "Mailchimp: Fetch subscribers with anniversaries approaching and their purchase data",
        280,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://usX.api.mailchimp.com/3.0/lists/{list_id}/members?count=100&since_timestamp_opt={anniversary_range_start}&before_timestamp_opt={anniversary_range_end}",
            headers: [
              { key: "Authorization", value: "Bearer {mailchimp_api_key}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [],
            variables: [
              {
                name: "members",
                customName: "anniversary_members",
                type: "object",
              },
              {
                name: "total_items",
                customName: "anniversary_count",
                type: "number",
              },
            ],
          },
        },
      ),
      n(
        "3",
        "condition",
        "Check: Customer has been with us for 2+ years?",
        520,
        {
          conditionDetails: {
            conditions: [
              { variable: "{years_as_customer}", operator: ">=", value: "2" },
            ],
          },
        },
      ),
      emailNode(
        "4",
        "Mailchimp: Send anniversary celebration email with loyalty reward",
        760,
        "{subscriber_email}",
        "Happy {years_as_customer}-Year Anniversary, {first_name}! A gift inside",
        "Hi {first_name},\n\nCan you believe it's been {years_as_customer} years since you joined us? We certainly can't — time flies when you're part of an amazing community!\n\nTo celebrate this milestone, here's your anniversary gift:\n\n20% off your next order with code ANNI{years_as_customer}0\n\nIn {years_as_customer} years, you've:\n- Placed {purchase_count} orders\n- Been a loyal member of our community\n\nThank you for choosing us, year after year. Here's to many more!\n\nCelebrate now: {shop_url}",
      ),
      n("5", "delayAction", "Wait 7 days", 1000, {
        delayDetails: { duration: 7, unit: "days" },
      }),
      n(
        "6",
        "condition",
        "Check: Anniversary reward has not been redeemed?",
        1240,
        {
          conditionDetails: {
            conditions: [
              { variable: "{reward_redeemed}", operator: "==", value: "false" },
            ],
          },
        },
      ),
      emailNode(
        "7",
        "Mailchimp: Send reward expiry reminder with curated picks",
        1480,
        "{subscriber_email}",
        "Your anniversary reward expires soon, {first_name}!",
        "Hi {first_name},\n\nYour 20% anniversary discount (code: ANNI{years_as_customer}0) expires in 3 days. Don't miss out on this special thank-you gift!\n\nWe picked a few items we think you'll love based on your purchase history:\n\n- {curated_pick_1}\n- {curated_pick_2}\n- {curated_pick_3}\n\nUse your reward: {shop_url}\n\nThank you for {years_as_customer} incredible years together!",
      ),
      sheetsNode(
        "8",
        "Google Sheets: Log anniversary milestone and redemption status",
        1720,
        "AnniversaryCampaigns",
        '[["{subscriber_email}", "{first_name}", "{years_as_customer}", "{purchase_count}", "{reward_redeemed}", "{order_value}", "{timestamp}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("7", "8"),
    ],
    summary:
      "Runs daily to identify subscribers with upcoming sign-up anniversaries, fetches their Mailchimp profiles and lifetime metrics, sends a celebration email with a 20% loyalty reward for 2+ year customers, waits 7 days, sends a redemption reminder with curated product picks, then logs the anniversary milestone and outcome to Google Sheets.",
  },

  // ─── 173. VIP Offer Journey (Mailchimp, Pattern H) ─────────────────────

  "VIP Offer Journey": {
    nodes: [
      trigger(
        "Shopify webhook: Customer tagged as VIP based on spend threshold",
        "webhook_app",
        {
          source: "shopify",
          event: "customers/update",
          howItStarts:
            "Fires when a Shopify customer is tagged as VIP after crossing the lifetime spend threshold (e.g., $1,000), providing the customer ID, email, total spent, order count, and new tags.",
          setup:
            "Connect your Shopify store and enable the customers/update webhook. Configure your VIP tagging rule in Shopify to fire when lifetime spend exceeds your threshold.",
          inputFields: [],
        },
      ),
      n(
        "2",
        "hitApi",
        "Mailchimp: Lookup subscriber profile, purchase history, and engagement",
        280,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://usX.api.mailchimp.com/3.0/lists/{list_id}/members/{subscriber_hash}?fields=merge_fields,stats,email_address",
            headers: [
              { key: "Authorization", value: "Bearer {mailchimp_api_key}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [],
            variables: [
              {
                name: "merge_fields.TOTAL_SPENT",
                customName: "total_spent",
                type: "string",
              },
              {
                name: "merge_fields.PURCHASE_COUNT",
                customName: "purchase_count",
                type: "number",
              },
              {
                name: "stats.avg_open_rate",
                customName: "avg_open_rate",
                type: "number",
              },
              {
                name: "merge_fields.FNAME",
                customName: "first_name",
                type: "string",
              },
            ],
          },
        },
      ),
      n(
        "3",
        "condition",
        "Check: Customer is a high-engagement VIP (open rate > 40%)?",
        520,
        {
          conditionDetails: {
            conditions: [
              { variable: "{avg_open_rate}", operator: ">", value: "40" },
            ],
          },
        },
      ),
      emailNode(
        "4",
        "Mailchimp: Send exclusive VIP welcome email with premium perks",
        760,
        "{subscriber_email}",
        "Welcome to VIP, {first_name}! Exclusive perks inside",
        "Hi {first_name},\n\nCongratulations — you've earned VIP status! With {purchase_count} orders and {total_spent} in lifetime purchases, you're one of our most valued customers.\n\nAs a VIP, you now enjoy:\n\n- Early access to all new collections\n- Exclusive 20% VIP discount (code: VIP20ELITE)\n- Free expedited shipping on every order\n- Priority customer support\n- Members-only flash sale invitations\n\nStart shopping with your new perks: {shop_url}\n\nYour VIP discount is valid for the next 14 days. Thank you for your loyalty!",
      ),
      n("5", "delayAction", "Wait 7 days", 1000, {
        delayDetails: { duration: 7, unit: "days" },
      }),
      n("6", "condition", "Check: VIP offer has not been redeemed?", 1240, {
        conditionDetails: {
          conditions: [
            {
              variable: "{vip_offer_redeemed}",
              operator: "==",
              value: "false",
            },
          ],
        },
      }),
      emailNode(
        "7",
        "Mailchimp: Send VIP offer reminder with next-tier teaser",
        1480,
        "{subscriber_email}",
        "{first_name}, your VIP 20% off expires in 7 days",
        "Hi {first_name},\n\nJust a reminder — your exclusive VIP 20% discount (code: VIP20ELITE) expires in 7 days.\n\nHere's what other VIP members are loving right now:\n\n- {trending_product_1}\n- {trending_product_2}\n- {trending_product_3}\n\nDid you know? VIP Diamond status is unlocked at {next_tier_threshold} in lifetime spending — you're only {amount_to_next_tier} away! Diamond members get 30% off and exclusive product launches.\n\nShop now: {shop_url}",
      ),
      sheetsNode(
        "8",
        "Google Sheets: Log VIP promotion and offer usage",
        1720,
        "VIPPromotions",
        '[["{subscriber_email}", "{first_name}", "{total_spent}", "{purchase_count}", "{vip_offer_redeemed}", "{order_value}", "{timestamp}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("7", "8"),
    ],
    summary:
      "Triggers when Shopify tags a customer as VIP after crossing the spend threshold, fetches their Mailchimp engagement metrics, sends high-engagement VIPs an exclusive welcome email with premium perks and 20% discount, waits 7 days, sends a redemption reminder with next-tier teaser, then logs the VIP promotion outcome to Google Sheets.",
  },

  // ─── 174. Win-Back Journey (Mailchimp, Pattern G) ──────────────────────

  "Win-Back Journey": {
    nodes: [
      trigger(
        "Schedule: Identify customers inactive for 120+ days",
        "schedule",
        {
          schedule: "Every Monday at 8:00 AM",
          howItStarts:
            "Runs weekly to identify Mailchimp subscribers who have not opened an email, clicked a link, or made a purchase in the last 120 days, pulling their last activity date, purchase history, and lifetime value.",
          setup:
            "Set up a weekly schedule trigger on Mondays at 8:00 AM. The workflow queries Mailchimp for subscribers with no activity in 120+ days.",
          inputFields: [],
        },
      ),
      n(
        "2",
        "hitApi",
        "Mailchimp: Fetch subscribers inactive for 120+ days with purchase history",
        280,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://usX.api.mailchimp.com/3.0/lists/{list_id}/members?count=200&since_last_changed={120_days_ago}&status=subscribed",
            headers: [
              { key: "Authorization", value: "Bearer {mailchimp_api_key}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [],
            variables: [
              {
                name: "members",
                customName: "inactive_members",
                type: "object",
              },
              {
                name: "total_items",
                customName: "inactive_count",
                type: "number",
              },
            ],
          },
        },
      ),
      aiNode(
        "3",
        "OpenAI: Analyze inactivity reasons and generate personalized win-back strategy",
        520,
        "You are a customer retention specialist. Given the inactive customer profile, purchase history, last activity date, and engagement metrics, analyze the likely reason for inactivity (e.g., found competitor, seasonal buyer, price sensitivity, product dissatisfaction). Generate a personalized win-back subject line, body copy, and recommended offer type. Return inactivity_reason, subject_line, winback_copy, recommended_offer, customer_segment.",
        "Analyze this inactive customer and suggest a win-back strategy: Email: {subscriber_email}, Last activity: {last_activity_date}, Purchase count: {purchase_count}, Total spent: {total_spent}, Avg order value: {avg_order_value}",
        [
          {
            field: "inactivity_reason",
            as: "inactivity_reason",
            type: "string",
          },
          { field: "subject_line", as: "subject_line", type: "string" },
          { field: "winback_copy", as: "winback_copy", type: "string" },
          {
            field: "recommended_offer",
            as: "recommended_offer",
            type: "string",
          },
          { field: "customer_segment", as: "customer_segment", type: "string" },
        ],
      ),
      n(
        "4",
        "condition",
        "Check: Was this a high-value customer (total spent > $300)?",
        760,
        {
          conditionDetails: {
            conditions: [
              { variable: "{total_spent}", operator: ">", value: "300" },
            ],
          },
        },
      ),
      emailNode(
        "5",
        "Mailchimp: Send personalized win-back email with we-miss-you offer",
        1000,
        "{subscriber_email}",
        "{subject_line}",
        "Hi {first_name},\n\nIt's been a while since we've seen you, and we genuinely miss having you around.\n\n{winback_copy}\n\nAs one of our valued customers, here's a special come-back offer just for you:\n\n{recommended_offer}\n\nUse code WEMISSYOU at checkout. This offer expires in 14 days.\n\nSee what's new: {shop_url}\n\nIf you'd rather not hear from us, no hard feelings — just click here to update your preferences.",
      ),
      n("6", "delayAction", "Wait 14 days", 1240, {
        delayDetails: { duration: 14, unit: "days" },
      }),
      n(
        "7",
        "condition",
        "Check: Customer is still inactive (no opens, clicks, or purchases)?",
        1480,
        {
          conditionDetails: {
            conditions: [
              { variable: "{reactivated}", operator: "==", value: "false" },
            ],
          },
        },
      ),
      emailNode(
        "8",
        "Mailchimp: Send sunset warning with preference update option",
        1720,
        "{subscriber_email}",
        "We're about to say goodbye, {first_name} — unless you want to stay",
        "Hi {first_name},\n\nWe've tried reaching out, but it seems like our emails aren't connecting with you anymore.\n\nTo keep your inbox clean, we'll be removing you from our mailing list in 7 days unless you tell us otherwise.\n\nWant to stay? Click here to confirm: {resubscribe_url}\n\nPrefer fewer emails? Update your preferences: {preferences_url}\n\nReady to shop again? Here's one last 20% off: code LASTONE20\n\nNo matter what you decide, thank you for being a customer. We hope to see you again!",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
    summary:
      "Runs weekly to find subscribers inactive for 120+ days, uses AI to analyze inactivity reasons and generate tailored win-back strategies, sends high-value customers a personalized we-miss-you email with a targeted offer, waits 14 days, then sends a sunset warning giving them a final chance to stay or update preferences before list removal.",
  },

  // ─── 175. Re-Engagement / Sunset (Mailchimp, Pattern G) ────────────────

  "Re-Engagement / Sunset": {
    nodes: [
      trigger(
        "Schedule: Identify subscribers with no email opens in 90 days",
        "schedule",
        {
          schedule: "Every Wednesday at 9:00 AM",
          howItStarts:
            "Runs weekly to identify Mailchimp subscribers who have not opened any email in the last 90 days, assessing their engagement history, purchase recency, and list membership duration.",
          setup:
            "Set up a weekly schedule trigger on Wednesdays at 9:00 AM. The workflow queries Mailchimp for subscribers with zero opens in the last 90 days.",
          inputFields: [],
        },
      ),
      n(
        "2",
        "hitApi",
        "Mailchimp: Fetch unengaged subscribers (no opens in 90 days)",
        280,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://usX.api.mailchimp.com/3.0/lists/{list_id}/members?count=200&status=subscribed&since_last_email_open={90_days_ago}",
            headers: [
              { key: "Authorization", value: "Bearer {mailchimp_api_key}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [],
            variables: [
              {
                name: "members",
                customName: "unengaged_members",
                type: "object",
              },
              {
                name: "total_items",
                customName: "unengaged_count",
                type: "number",
              },
            ],
          },
        },
      ),
      aiNode(
        "3",
        "OpenAI: Analyze subscriber engagement decay and recommend re-engagement tactic",
        520,
        "You are an email deliverability and re-engagement specialist. Given the subscriber profile, historical open rates, click rates, last open date, and purchase history, determine the best re-engagement tactic: content refresh, frequency change, incentive, or sunset. Generate a subject line designed to break through inbox blindness and body copy with a clear value proposition. Return re_engagement_tactic, subject_line, body_copy, predicted_recovery_chance.",
        "Analyze this unengaged subscriber and recommend a re-engagement tactic: Email: {subscriber_email}, Last open: {last_open_date}, Historical open rate: {historical_open_rate}, Purchase count: {purchase_count}, List tenure: {list_tenure_months} months",
        [
          {
            field: "re_engagement_tactic",
            as: "re_engagement_tactic",
            type: "string",
          },
          { field: "subject_line", as: "subject_line", type: "string" },
          { field: "body_copy", as: "body_copy", type: "string" },
          {
            field: "predicted_recovery_chance",
            as: "recovery_chance",
            type: "string",
          },
        ],
      ),
      n(
        "4",
        "condition",
        "Check: Subscriber has any purchase history (purchase count > 0)?",
        760,
        {
          conditionDetails: {
            conditions: [
              { variable: "{purchase_count}", operator: ">", value: "0" },
            ],
          },
        },
      ),
      emailNode(
        "5",
        "Mailchimp: Send re-engagement email with updated value proposition",
        1000,
        "{subscriber_email}",
        "{subject_line}",
        "Hi {first_name},\n\nWe noticed you haven't been opening our emails lately, and we want to make sure we're sending you the right stuff.\n\n{body_copy}\n\nHere's what you've been missing:\n\n- New arrivals hand-picked for you\n- Exclusive deals for our email community\n- Product tips and inspiration\n\nStay connected: {resubscribe_url}\n\nUpdate what you receive: {preferences_url}\n\nIf we don't hear from you in the next 14 days, we'll pause your emails to keep your inbox clean.",
      ),
      n("6", "delayAction", "Wait 14 days", 1240, {
        delayDetails: { duration: 14, unit: "days" },
      }),
      n(
        "7",
        "condition",
        "Check: Subscriber still has zero engagement (no opens or clicks)?",
        1480,
        {
          conditionDetails: {
            conditions: [
              { variable: "{re_engaged}", operator: "==", value: "false" },
            ],
          },
        },
      ),
      n(
        "8",
        "hitApi",
        "Mailchimp: Move subscriber to suppressed/unsubscribed status",
        1720,
        {
          hitApiDetails: {
            method: "PATCH",
            url: "https://usX.api.mailchimp.com/3.0/lists/{list_id}/members/{subscriber_hash}",
            headers: [
              { key: "Authorization", value: "Bearer {mailchimp_api_key}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              { key: "status", value: "unsubscribed" },
              {
                key: "merge_fields",
                value:
                  '{"SUNSET_DATE": "{today}", "SUNSET_REASON": "90-day no opens, failed re-engagement"}',
              },
            ],
            variables: [
              { name: "status", customName: "new_status", type: "string" },
              {
                name: "email_address",
                customName: "suppressed_email",
                type: "string",
              },
            ],
          },
        },
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
    summary:
      "Runs weekly to find subscribers with no email opens in 90 days, uses AI to analyze engagement decay and recommend the best re-engagement tactic, sends a tailored re-engagement email with updated value proposition and preference management options, waits 14 days, then suppresses still-unengaged subscribers in Mailchimp to protect sender reputation and list hygiene.",
  },
};
